import { useEffect } from 'react';
import { useLocation } from 'react-use';
import { Link } from '@hotjar/hotsauce';
import { useStickyNotification } from '@hotjar/navigation';
import { Trans } from '@hotjar/translations';
import type { CurrentUser } from '@hotjar/user';
import { useCurrentUser } from '@hotjar/user';
import {
  ALERTS_CTA,
  ELEMENT_NAME,
  trackClick,
  trackViewElement,
} from '~/subscription-management/utils/tracking';
import { PAYMENT_DETAILS } from '~/subscription-management/constants/routes';
import { useSubscriptionStatus } from '~/subscription-management/queries';
import {
  LEGACY_SUBSCRIPTION_ISSUE_ERROR,
  OVERDUE_ACCOUNT_OWNER_ERROR,
} from './paymentOverdueAlertUtils';

export interface OwnedAccountInfo {
  isOwnedAccount: boolean;
  accountId: number | null;
}

/**
 * Selector for `useCurrentUser` to return a boolean for whether the user owns an account and the
 * account id if they have one. (Returns `undefined` if the user data is not loaded yet).
 */
export const ownedAccountInfoSelector = (
  currentUser: CurrentUser | undefined,
): OwnedAccountInfo => {
  if (currentUser?.accounts != null && currentUser.accounts.length > 0) {
    return {
      isOwnedAccount: true,
      accountId: currentUser.accounts[0].id,
    };
  }

  return {
    isOwnedAccount: false,
    accountId: null,
  };
};

export const usePaymentOverdueAccountOwnerAlert = (): void => {
  const { hide, show } = useStickyNotification();
  const location = useLocation();
  const { data: ownedAccountInfo } = useCurrentUser(ownedAccountInfoSelector);
  const { isOwnedAccount = false, accountId = null } = ownedAccountInfo ?? {};
  const { data: subscriptionStatus } = useSubscriptionStatus(accountId, {
    enabled: isOwnedAccount,
  });

  useEffect(() => {
    const { status } = subscriptionStatus ?? {};

    // check if we should show the alert.
    if (
      accountId !== null &&
      isOwnedAccount &&
      status === 'OVERDUE' &&
      location.pathname?.includes(PAYMENT_DETAILS) !== true
    ) {
      // hide the legacy alert, in case it also shows.
      hide(LEGACY_SUBSCRIPTION_ISSUE_ERROR);

      // show the alert.
      show(OVERDUE_ACCOUNT_OWNER_ERROR, {
        kind: 'danger',
        isDismissible: false,
        text: (
          <Trans
            i18nKey="checkout:alerts.payment-overdue.account-owner"
            components={[
              <Link
                kind="inline"
                href={PAYMENT_DETAILS}
                onClick={() => {
                  trackClick({
                    eventName: ALERTS_CTA.updatePaymentDetails,
                    uiPosition: 'Account owner payment overdue alert',
                    accountId,
                  });
                }}
              >
                update your payment details
              </Link>,
            ]}
          />
        ),
      });

      // send the view event.
      trackViewElement({
        name: ELEMENT_NAME.globalAccountOwnerOverdueAlert,
        accountId,
        uiPosition: 'Navigation',
      });
    } else {
      // if we shouldn't show the alert, hide it, in case it was already showing.
      hide(OVERDUE_ACCOUNT_OWNER_ERROR);
    }

    return () => {
      // hide alert on unmount.
      hide(OVERDUE_ACCOUNT_OWNER_ERROR);
    };
  }, [location.pathname, subscriptionStatus, isOwnedAccount, accountId, hide, show]);
};
